import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export type SidebarSide = 'left' | 'right';

interface LayoutState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  sidebarSide: SidebarSide;
  setSidebarSide: (side: SidebarSide) => void;
}

const SIDEBAR_SIDE_KEY = 'vidnight-sidebar-side';

const LayoutContext = createContext<LayoutState | null>(null);

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarSide, setSidebarSideState] = useState<SidebarSide>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_SIDE_KEY);
      return stored === 'right' ? 'right' : 'left';
    } catch {
      return 'left';
    }
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const setSidebarSide = useCallback((side: SidebarSide) => {
    setSidebarSideState(side);
    try { localStorage.setItem(SIDEBAR_SIDE_KEY, side); } catch { /* noop */ }
  }, []);

  return (
    <LayoutContext.Provider
      value={{ sidebarCollapsed, toggleSidebar, setSidebarCollapsed, sidebarSide, setSidebarSide }}
    >
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('useLayout must be used within LayoutProvider');
  return context;
}
